import { useEffect } from "react";
import { toast } from "sonner";
import { getNotificationRoute } from "@/components/notifications/notification-routing";
import { showReportExportReadyToast } from "@/components/notifications/report-export-ready-toast";
import { cashflowReportExportClient, invoiceExportClient, payoutReportExportClient, type ReportExportClient, unpaidCompletedWorkReportExportClient } from "@/lib/report-export-clients";
import { reportExportsOnScreen } from "@/lib/report-exports-on-screen";
import type { SignalRNotification } from "@/lib/signalr";
import { router } from "@/router";
import { useSignalRConnection } from "./useSignalR";

const reportExportClients: Record<string, ReportExportClient> = {
  "cashflow-report-export-ready-admin": cashflowReportExportClient,
  "invoice-ready-admin": invoiceExportClient,
  "payout-report-ready-admin": payoutReportExportClient,
  "unpaid-completed-work-report-export-ready-admin": unpaidCompletedWorkReportExportClient,
};

const toastMethods: Record<string, typeof toast.info> = {
  info: toast.info,
  success: toast.success,
  warning: toast.warning,
  error: toast.error,
};

export function useSignalRNotifications() {
  const connection = useSignalRConnection();

  useEffect(() => {
    if (!connection) return;

    const handler = (notification: SignalRNotification) => {
      const showToast = toastMethods[notification.type] ?? toast.info;
      const route = getNotificationRoute(notification.typeKey, notification.subjectId);

      const reportExportClient = reportExportClients[notification.typeKey];

      if (reportExportClient && notification.subjectId) {
        if (!reportExportsOnScreen.has(notification.subjectId)) {
          void showReportExportReadyToast(showToast, notification, notification.subjectId, reportExportClient);
        }

        return;
      }

      showToast(notification.title, {
        description: notification.message,
        action: route ? { label: "Open", onClick: () => router.history.push(route) } : undefined,
        duration: route ? Infinity : undefined,
      });
    };

    connection.on("ReceiveNotification", handler);
    return () => connection.off("ReceiveNotification", handler);
  }, [connection]);
}
